import React, { useEffect, useRef, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import debounce from 'lodash.debounce';
import styles from './CustomDropdown.module.scss';
import cn from 'classnames';
import Arrow from '../../Icons/Arrow';

export default function CustomDropdown({
  options,
  placeholderValue,
  selectedValue,
  isOptionsAbsolutePosition,
  onSelect,
  searchEmpty,
  disabled = false,
  onSearchChange,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(selectedValue || null);
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // 计算输入与 postCode 的最长公共前缀长度，作为匹配得分
  const getPrefixScore = (postCode, keyword) => {
    if (!postCode) return 0;
    const code = String(postCode);
    let score = 0;
    while (score < keyword.length && score < code.length && code[score] === keyword[score]) {
      score += 1;
    }
    return score;
  };

  // 监听搜索值变化，按公共前缀得分排序（0 分项排最后，同分保持原始顺序）
  useEffect(() => {
    const keyword = searchValue.trim();
    if (keyword === '') {
      setFilteredOptions(options); // 搜索值为空时显示所有选项
    } else {
      const sorted = options
        .map((option, index) => ({
          option,
          index,
          score: getPrefixScore(option.postCode, keyword),
        }))
        .sort((a, b) => b.score - a.score || a.index - b.index)
        .map(({ option }) => option);
      setFilteredOptions(sorted);
    }
  }, [searchValue, options]);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen((prev) => {
        const newIsOpen = !prev;
        if (newIsOpen) {
          // 展开时清空搜索值，确保显示所有选项
          setSearchValue('');
          setTimeout(() => {
            inputRef.current?.focus();
          }, 100);
        }
        return newIsOpen;
      });
    }
  };

  const handleInputChange = (e) => {
    setSearchValue(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
    // 用户输入时（防抖 2 秒）把搜索词暴露给父组件，用于触发接口查询
    debouncedSearchChange(e.target.value);
  };

  const handleOptionClick = (option) => {
    setSelected(option);
    setSearchValue('');
    setIsOpen(false);
    if (onSelect) {
      onSelect(option);
    }
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchValue('');
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // 监听 selectedValue 变化
  useEffect(() => {
    setSelected(selectedValue || null);
  }, [selectedValue]);

  // 防抖 2 秒的搜索词回调（仅用户输入时触发，程序化清空不触发）
  const debouncedSearchChange = useMemo(
    () => debounce((keyword) => onSearchChange?.(keyword), 300),
    [onSearchChange]
  );

  // 组件卸载时取消防抖，避免泄漏
  useEffect(() => {
    return () => {
      debouncedSearchChange.cancel();
    };
  }, [debouncedSearchChange]);

  return (
    <div className={styles.dropdown} ref={dropdownRef}>
      <div
        className={cn(
          styles.selected,
          isOpen && styles.selected_active,
          disabled && styles.selected_disabled
        )}
        onClick={toggleDropdown}
      >
        <input
          ref={inputRef}
          type="text"
          className={styles.searchInput}
          value={isOpen ? searchValue : selected ? selected.label : ''}
          onChange={handleInputChange}
          placeholder={placeholderValue}
          disabled={disabled}
        />
        <span className={styles.arrow}>
          <Arrow isRotated={isOpen} color="#121212" strokeWidth="1" />
        </span>
      </div>
      {!disabled && (
        <motion.ul
          className={cn(
            styles.options,
            isOptionsAbsolutePosition && styles.options_positionAbsolute
          )}
          initial={{ height: 0, opacity: 0 }}
          animate={isOpen ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          style={{ overflow: 'overlay' }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={`${option.value}-${index}`}
                className={styles.option}
                onClick={() => handleOptionClick(option)}
              >
                {option.label}
              </li>
            ))
          ) : (
            <li className={styles.option}>{searchEmpty}</li>
          )}
        </motion.ul>
      )}
    </div>
  );
}
